import React, { useState } from "react";
import api from "../api/api";
import { Star } from "lucide-react";

export default function FeedbackModal({ interviewId, existingFeedback, onClose, onSave }) {
  const [rating, setRating] = useState(existingFeedback?.rating || 0);
  const [difficulty, setDifficulty] = useState(existingFeedback?.difficulty || "Medium");
  const [comments, setComments] = useState(existingFeedback?.comments || "");
  const [loading, setLoading] = useState(false);
  const [showThanks, setShowThanks] = useState(false);

  const difficulties = ["Easy", "Medium", "Hard"];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post(`/feedback/${interviewId}`, {
        rating,
        difficulty,
        comments,
      });
      onSave(res.data);

      // ✅ show thanks popup instead of closing immediately
      setShowThanks(true);
    } catch (err) {
      console.error(err);
      alert("Failed to save feedback");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseThanks = () => {
    setShowThanks(false);
    onClose(); // close modal after thanks popup
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
      {/* Feedback Form */}
      {!showThanks && (
        <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-md">
          <h2 className="text-xl font-bold mb-4">
            {existingFeedback ? "Edit Your Feedback" : "Give Feedback"}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* ⭐ Star Rating */}
            <div>
              <label className="block font-medium mb-1">Rating</label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    className="focus:outline-none"
                  >
                    <Star
                      className={`w-7 h-7 ${
                        rating >= star
                          ? "text-yellow-400 fill-yellow-400"
                          : "text-gray-300"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* 🎯 Difficulty */}
            <div>
              <label className="block font-medium mb-1">Difficulty</label>
              <select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
              >
                {difficulties.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>

            {/* 📝 Comments */}
            <div>
              <label className="block font-medium mb-1">Comments</label>
              <textarea
                rows={3}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Share your feedback..."
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg border"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-5 py-2 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:opacity-90"
              >
                {loading ? "Saving..." : "Submit"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ✅ Thanks Popup */}
      {showThanks && (
        <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-sm text-center">
          <h2 className="text-2xl font-bold mb-2">🎉 Thanks!</h2>
          <p className="text-gray-600 mb-4">
            Your interview feedback has been submitted successfully.
          </p>
          <button
            onClick={handleCloseThanks}
            className="px-5 py-2 rounded-lg bg-indigo-500 text-white hover:bg-indigo-600"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}
